import type { DropOffLocation } from '../../../../core/api/locator/models/locator.model';
import type {
  PickupAddress,
  PickupAvailabilitySlot,
  SchedulePickupRequest,
} from '../../../../core/api/pickup/models/pickup.model';
import type { AddressFormValue } from '../../../ship-from-ship-to/models/address-form.model';
import { normalizePhoneDigits } from '../../../ship-from-ship-to/validators/phone.validators';
import { containsDisallowedEmoji } from '../../../../shared/validators/emoji.validators';
import { isSelectablePickupLocationCode } from './mappers/build-pickup-location-options';

/** Induction method chosen on the Service step (mutually exclusive). */
export type InductionMethod = 'drop-off' | 'label-only' | 'pickup';

/** Max length for the pickup courier instructions field. */
export const MAX_COURIER_INSTRUCTIONS_LENGTH = 120;

/** Default Salesforce street type when none can be parsed from the civic line. */
export const DEFAULT_PICKUP_STREET_TYPE = 'ST';

/**
 * Maps common Canadian street-type words/abbreviations to Salesforce streetType codes.
 * Used so validate payloads match the live pickup contract (`streetName` + `streetType`).
 */
const STREET_TYPE_CODES: Record<string, string> = {
  st: 'ST',
  street: 'ST',
  ave: 'AVE',
  avenue: 'AVE',
  rd: 'RD',
  road: 'RD',
  blvd: 'BLVD',
  boulevard: 'BLVD',
  dr: 'DR',
  drive: 'DR',
  cres: 'CRES',
  crescent: 'CRES',
  crt: 'CRT',
  court: 'CRT',
  pl: 'PL',
  place: 'PL',
  way: 'WAY',
  ln: 'LN',
  lane: 'LN',
  hwy: 'HWY',
  highway: 'HWY',
  cir: 'CIR',
  circle: 'CIR',
  terr: 'TERR',
  terrace: 'TERR',
};

const STREET_DIRECTIONS = new Set([
  'n',
  's',
  'e',
  'w',
  'north',
  'south',
  'east',
  'west',
  'ne',
  'nw',
  'se',
  'sw',
]);

/**
 * Pickup/drop-off feature state for the Service step.
 * Time window fields (`windowFrom` / `windowTo`) are HHmm strings — 24h digits, no colon,
 * e.g. `"1430"` for 2:30 PM — matching the live `POST /pickup/validate` contract.
 */
export interface PickupDropOffState {
  method: InductionMethod;
  /** yyyy-MM-dd */
  selectedDate: string | null;
  /** HHmm, no colon */
  windowFrom: string | null;
  /** HHmm, no colon */
  windowTo: string | null;
  locationCode: string | null;
  /** Max 120 characters. */
  courierInstructions: string;
  /** Validate `pickUps` slots (live BE shape); null until loaded. */
  availability: PickupAvailabilitySlot[] | null;
  pickupFromMap: Record<string, string> | null;
  nearestDropOff: DropOffLocation | null;
}

/**
 * Returns the default Service-step pickup/drop-off state (drop-off active, no pickup fields set).
 */
export function createInitialPickupDropOffState(): PickupDropOffState {
  return {
    method: 'drop-off',
    selectedDate: null,
    windowFrom: null,
    windowTo: null,
    locationCode: null,
    courierInstructions: '',
    availability: null,
    pickupFromMap: null,
    nearestDropOff: null,
  };
}

/**
 * Switches the active induction method while retaining previously entered pickup fields,
 * so toggling Drop-off / Label-only / Pickup does not discard valid data for the active shipment.
 */
export function setInductionMethod(
  state: PickupDropOffState,
  method: InductionMethod,
): PickupDropOffState {
  if (state.method === method) {
    return state;
  }

  return { ...state, method };
}

/** Outbound Service-step save payload — pickup fields are present only when method is pickup. */
export interface PickupDropOffSavePayload {
  method: InductionMethod;
  selectedDate?: string;
  windowFrom?: string;
  windowTo?: string;
  locationCode?: string;
  /** Resolved pickup-from label from `GET /pickup/locations` at save time (locale of the save). */
  locationLabel?: string;
  courierInstructions?: string;
  /** Prepared `POST /pickup/schedule` body for downstream scheduling (pickup only). */
  scheduleRequest?: SchedulePickupRequest;
  /**
   * Selected nearest drop-off location when method is drop-off.
   * Persisted so Review can submit `dropOffInformation` when locator search succeeded;
   * omitted when locator is down so create can still send `pickupType=DROP_OFF` alone.
   */
  nearestDropOff?: DropOffLocation;
  /**
   * Retained pickup field draft when the active method is Drop-off or Create label only,
   * so navigating back can restore Schedule a pickup without re-submitting pickup.
   */
  pickupDraft?: PickupDraftFields;
}

/** Pickup field snapshot retained alongside a non-pickup induction method. */
export interface PickupDraftFields {
  selectedDate: string | null;
  windowFrom: string | null;
  windowTo: string | null;
  locationCode: string | null;
  courierInstructions: string;
}

/**
 * Extracts a pickup draft from UI state when any pickup field has been entered.
 * @returns Draft fields, or undefined when nothing has been entered yet.
 */
export function extractPickupDraft(state: PickupDropOffState): PickupDraftFields | undefined {
  const hasDraft =
    state.selectedDate != null ||
    state.windowFrom != null ||
    state.windowTo != null ||
    state.locationCode != null ||
    state.courierInstructions.length > 0;

  if (!hasDraft) {
    return undefined;
  }

  return {
    selectedDate: state.selectedDate,
    windowFrom: state.windowFrom,
    windowTo: state.windowTo,
    locationCode: state.locationCode,
    courierInstructions: state.courierInstructions,
  };
}

/**
 * Builds the outbound pickup/drop-off save payload for the active method only.
 * Drop-off and create-label-only never include top-level pickup fields — induction methods are
 * mutually exclusive per shipment — but may carry `pickupDraft` for UI retention.
 * Drop-off also persists `nearestDropOff` so Review can submit `dropOffInformation`.
 */
export function buildPickupDropOffSavePayload(state: PickupDropOffState): PickupDropOffSavePayload {
  if (state.method === 'drop-off' || state.method === 'label-only') {
    const pickupDraft = extractPickupDraft(state);
    const payload: PickupDropOffSavePayload = pickupDraft
      ? { method: state.method, pickupDraft }
      : { method: state.method };
    if (state.method === 'drop-off' && state.nearestDropOff) {
      payload.nearestDropOff = state.nearestDropOff;
    }
    return payload;
  }

  const payload: PickupDropOffSavePayload = { method: 'pickup' };

  if (state.selectedDate) {
    payload.selectedDate = state.selectedDate;
  }

  if (state.windowFrom) {
    payload.windowFrom = state.windowFrom;
  }

  if (state.windowTo) {
    payload.windowTo = state.windowTo;
  }

  if (state.locationCode) {
    payload.locationCode = state.locationCode;
    const label = state.pickupFromMap?.[state.locationCode]?.trim();
    if (label) {
      payload.locationLabel = label;
    }
  }

  if (state.courierInstructions) {
    payload.courierInstructions = state.courierInstructions;
  }

  return payload;
}

/**
 * Restores Service-step pickup/drop-off UI state from a persisted save payload so the
 * customer keeps their selection when navigating away and returning during the shipment.
 * Non-pickup methods restore fields from `pickupDraft` when present.
 */
export function hydratePickupDropOffFromSavePayload(
  payload: PickupDropOffSavePayload,
): PickupDropOffState {
  const base = createInitialPickupDropOffState();

  if (payload.method === 'pickup') {
    return {
      ...base,
      method: 'pickup',
      selectedDate: payload.selectedDate ?? null,
      windowFrom: payload.windowFrom ?? null,
      windowTo: payload.windowTo ?? null,
      locationCode: payload.locationCode ?? null,
      courierInstructions: payload.courierInstructions ?? '',
    };
  }

  const draft = payload.pickupDraft;
  return {
    ...base,
    method: payload.method,
    selectedDate: draft?.selectedDate ?? null,
    windowFrom: draft?.windowFrom ?? null,
    windowTo: draft?.windowTo ?? null,
    locationCode: draft?.locationCode ?? null,
    courierInstructions: draft?.courierInstructions ?? '',
    nearestDropOff: payload.method === 'drop-off' ? (payload.nearestDropOff ?? null) : null,
  };
}

/**
 * Validates the active induction method only.
 * Drop-off is always valid (locator address is optional when search fails).
 * Create-label-only has no required fields.
 * Pickup requires a date, full time window, and a mapped Salesforce pickup location code
 * (Please Select is not valid). Courier instructions must not contain emoji.
 */
export function isPickupDropOffValid(state: PickupDropOffState): boolean {
  if (state.method === 'label-only' || state.method === 'drop-off') {
    return true;
  }

  if (containsDisallowedEmoji(state.courierInstructions)) {
    return false;
  }

  return Boolean(
    state.selectedDate &&
    state.windowFrom &&
    state.windowTo &&
    isSelectablePickupLocationCode(state.locationCode, state.pickupFromMap),
  );
}

/**
 * Maps the Ship From address form value to the pickup API's `PickupAddress` shape.
 * Aligns with the live `POST /pickup/validate` / Salesforce contact contract:
 * digits-only phone, non-empty lastName, `streetName` without type, and `streetType` code.
 */
export function mapShipFromToPickupAddress(shipFrom: AddressFormValue): PickupAddress {
  const { firstName, lastName } = splitFullName(shipFrom.companyOrFullName);
  const streetParts = resolvePickupStreetParts(shipFrom);
  const { streetName, streetType } = splitStreetNameAndType(streetParts.streetName);

  return {
    name: shipFrom.companyOrFullName.trim(),
    firstName,
    lastName,
    streetNumber: streetParts.streetNumber,
    streetName,
    streetType,
    city: shipFrom.city.trim(),
    postalCode: shipFrom.postalCode.replace(/\s+/g, ''),
    provinceCode: (shipFrom.province ?? '').trim().toUpperCase(),
    countryCode: 'CA',
    phoneNumber: normalizePhoneDigits(shipFrom.phoneNumber),
    email: shipFrom.email.trim(),
  };
}

/** Splits a combined full name into first/last; duplicates first when only one word (SF requires both). */
function splitFullName(fullName: string): { firstName: string; lastName: string } {
  const trimmed = fullName.trim();
  const spaceIndex = trimmed.indexOf(' ');

  if (spaceIndex === -1) {
    return { firstName: trimmed, lastName: trimmed };
  }

  return {
    firstName: trimmed.slice(0, spaceIndex),
    lastName: trimmed.slice(spaceIndex + 1).trim(),
  };
}

/**
 * Resolves pickup `streetNumber` / raw street remainder from Ship From line 1,
 * falling back to address line 2 when line 1 has no leading civic street number.
 */
function resolvePickupStreetParts(shipFrom: AddressFormValue): {
  streetNumber: string;
  streetName: string;
} {
  const fromLine1 = splitStreetAddress(shipFrom.streetAddress);
  if (fromLine1.streetNumber && fromLine1.streetName) {
    return fromLine1;
  }

  const line2 = shipFrom.addressLine2.trim();
  if (line2) {
    const fromLine2 = splitStreetAddress(line2);
    if (fromLine2.streetNumber && fromLine2.streetName) {
      return fromLine2;
    }

    if (fromLine1.streetNumber && !fromLine1.streetName) {
      return { streetNumber: fromLine1.streetNumber, streetName: line2 };
    }

    if (!fromLine1.streetNumber && fromLine2.streetNumber) {
      return fromLine2;
    }
  }

  // Pickup validate requires non-empty streetNumber; use a sentinel when line 1 has no
  // parseable civic number and no civic addressLine2 (e.g. special-address keywords only).
  if (!fromLine1.streetNumber && fromLine1.streetName) {
    return { streetNumber: '0', streetName: fromLine1.streetName };
  }

  return fromLine1;
}

/**
 * Splits a leading civic street number from the remainder of a street address.
 * Supports plain digits (`40 King`), alphanumeric (`123A Main`), and AVS unit-street
 * tokens (`65-12560 WESTSIDE RD`). Hyphenated unit-street values use only the civic
 * number after the hyphen — Salesforce rejects `-` in `streetNumber`.
 */
function splitStreetAddress(streetAddress: string): { streetNumber: string; streetName: string } {
  const trimmed = streetAddress.trim();
  if (!trimmed) {
    return { streetNumber: '', streetName: '' };
  }

  const spaceMatch = /\s+/.exec(trimmed);
  if (spaceMatch && spaceMatch.index > 0) {
    const candidate = trimmed.slice(0, spaceMatch.index);
    const streetName = trimmed.slice(spaceMatch.index).trim();
    if (/^\d[\w-]*$/.test(candidate) && streetName) {
      return {
        streetNumber: normalizePickupStreetNumber(candidate),
        streetName,
      };
    }
  }

  if (/^\d[\w-]*$/.test(trimmed)) {
    return { streetNumber: normalizePickupStreetNumber(trimmed), streetName: '' };
  }

  return { streetNumber: '', streetName: trimmed };
}

/**
 * Returns a Salesforce-safe street number: for `unit-civic` tokens, keep only the civic
 * segment after the last hyphen (e.g. `123-45` → `45`, `65-12560` → `12560`).
 */
function normalizePickupStreetNumber(streetNumber: string): string {
  const trimmed = streetNumber.trim();
  if (!trimmed.includes('-')) {
    return trimmed;
  }

  const civic = trimmed.slice(trimmed.lastIndexOf('-') + 1).trim();
  return civic || trimmed.replaceAll('-', '');
}

/**
 * Separates a trailing street-type token (and optional direction) so Salesforce receives
 * `streetName` + `streetType` separately — matching BE fixtures (`Main` + `ST`).
 */
function splitStreetNameAndType(streetNameWithType: string): {
  streetName: string;
  streetType: string;
} {
  const trimmed = streetNameWithType.trim();
  if (!trimmed) {
    return { streetName: '', streetType: DEFAULT_PICKUP_STREET_TYPE };
  }

  const parts = trimmed.split(/\s+/);
  if (parts.length < 2) {
    return { streetName: trimmed, streetType: DEFAULT_PICKUP_STREET_TYPE };
  }

  let direction = '';
  let typeIndex = parts.length - 1;
  const last = parts.at(-1)?.toLowerCase() ?? '';

  if (STREET_DIRECTIONS.has(last) && parts.length >= 3) {
    direction = parts.at(-1) ?? '';
    typeIndex = parts.length - 2;
  }

  const typeToken = parts[typeIndex]?.toLowerCase() ?? '';
  const streetType = STREET_TYPE_CODES[typeToken];

  if (!streetType || typeIndex < 1) {
    return { streetName: trimmed, streetType: DEFAULT_PICKUP_STREET_TYPE };
  }

  const nameParts = parts.slice(0, typeIndex);
  if (direction) {
    nameParts.push(direction);
  }

  return {
    streetName: nameParts.join(' '),
    streetType,
  };
}
